import { Injectable } from '@angular/core';
import {
  DEFAULT_LEVEL_FILTER,
  DEFAULT_SEARCH_ORDER,
  LevelFilter,
  SearchOrder,
  theLevelAndOrderToSend,
} from '../domains/the-level-filter-and-the-search-order';
import { Action, State, StateContext, Store } from '@ngxs/store';
import {
  AddSportSelectedToFilterEventsAction,
  FilterByDateExploreAction,
  FilterByDateExploreEventsAction,
  FilterByDateRangeExploreEventsAction,
  FilterBySpecificDateExploreEventsAction,
  FilterDateTypeExplore,
  FilterExploreEventsAction,
  LoadExploreEventsAction,
  LoadExploreEventsActionWithoutMe,
  RemoveSportSelectedToFilterEventsAction,
  ResetByDateExploreEventsAction,
  ResetFilterEventsAction,
  SendFilterEventsAction,
  SetDistanceFiltersEventAction,
  SetDurationFiltersEventAction,
  SetFilterByDateExploreEventsAction,
  SetLoadingEventsAction,
  SetLocationFilterEventsAction,
  SetLevelFilterEventsAction,
  SetHandiFriendlyOnlyFilterEventsAction,
  SetPartnersOnlyFilterEventsAction,
  UpdateRadiusExploreAction,
} from './filter-events.actions';
import { ActiveEventDetailOutputDto, EventsService, FilterDate, FilterEventDto } from '@smatchy/sdk';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { UserProfileSelector } from '../../user/state/user-profile.selector';
import { add, eachWeekendOfInterval, endOfDay, isWeekend, startOfDay, startOfToday, nextSaturday } from 'date-fns';
import { GeoPosition } from '../../locations';
import {
  DEFAULT_EXPLORE_RADIUS_KM,
  theRadiusSearchedMeter,
  theSearchMayWiden,
} from '../domains/the-explore-radius-widens-only-when-nobody-chose-it';
import { GeoLocality } from '../../locations/domains/geo-locality';
import { FilterEventsSelector } from './filter-events.selector';
import { buildLocationSearchFields } from '../../../utils/build-location-search-fields.util';
import { AnalyticsService } from '../../../services/analytics.service';
import { JourneyEventService } from '../../../services/journey-event.service';

const resolveFilterPosition = (
  location?: FilterEventsStateModel['location'],
): { lat: number; lng: number } | undefined => {
  const position = location?.position;

  if (position?.lat === undefined || position?.lat === null) {
    return undefined;
  }

  const lng = position.lng ?? (position as { lon?: number }).lon;

  if (lng === undefined || lng === null) {
    return undefined;
  }

  return { lat: position.lat, lng };
};

const getStartEndStartFromFilterDateType = (
  // Optional, because one caller passes nothing to mean "no date type asked
  // for", which the switch's own default branch already answers.
  dateType?: FilterDateTypeExplore,
): {
  startDate: string;
  endDate: string;
} => {
  let startDate = startOfToday().toISOString();
  let endDate;
  switch (dateType) {
    case 'today':
      startDate = startOfToday().toISOString();
      endDate = endOfDay(startOfToday()).toISOString();
      break;
    case 'tomorrow': {
      const tomorrow = add(startOfToday(), { days: 1 });
      startDate = startOfDay(tomorrow).toISOString();
      // `.toISOString()` like every other branch. This one alone handed back a
      // Date object, which the state declares as a string and which only
      // survived because JSON.stringify turns a Date into the same ISO string
      // on its way to the API.
      endDate = endOfDay(tomorrow).toISOString();
      break;
    }
    case 'week': {
      const week = add(startOfToday(), { days: 7 });
      startDate = startOfDay(startOfToday()).toISOString();
      endDate = endOfDay(week).toISOString();
      break;
    }
    case 'week-end':
      if (isWeekend(startOfToday())) {
        const saturdaySunday: Date[] = eachWeekendOfInterval({
          start: startOfDay(startOfToday()),
          end: add(endOfDay(startOfToday()), { days: 1 }),
        });
        startDate = saturdaySunday[0].toISOString();
        endDate = endOfDay(saturdaySunday[saturdaySunday.length - 1]).toISOString();
      } else {
        const saturdaySunday: Date[] = eachWeekendOfInterval({
          start: startOfDay(nextSaturday(startOfToday())),
          end: add(endOfDay(nextSaturday(startOfToday())), { days: +1 }),
        });
        startDate = saturdaySunday[0].toISOString();
        endDate = endOfDay(saturdaySunday[saturdaySunday.length - 1]).toISOString();
      }
      break;

    default:
      startDate = startOfToday().toISOString();
      // `.toISOString()` like every other branch. The second of the two that
      // handed back a Date object where the state declares a string; the first
      // was 'tomorrow'.
      endDate = add(startOfToday(), { days: 365 }).toISOString();
      break;
  }
  return { startDate, endDate };
};

export const radiusKmToMeter = (value: number) => value * 1000;
export const radiusMeterToKm = (value: number) => value / 1000;

interface FilterRangeNumber {
  min: number;
  max: number;
}

export interface FilterEventsStateModel {
  loading: boolean;
  /*
   * `| null` because that is what the state is actually set to. Three actions
   * clear the dates by writing null -- filtering by a date type, resetting the
   * date filter, and the "all" branch -- and the model said `string`. Written
   * rather than changed to undefined: the value is persisted to localStorage,
   * where null survives a round trip and undefined does not.
   */
  startDate: string | null;
  endDate?: string | null;
  eventsWrappedBySport: { sportKeyId: string; events: ActiveEventDetailOutputDto[] }[];
  events: ActiveEventDetailOutputDto[];
  filtersDateTypeExplore: FilterDateTypeExplore[];
  radius: number;
  /**
   * True when `radius` came from the person rather than from a default. A
   * persisted state from before this field reads as undefined, which is false:
   * a radius nobody marked as chosen. The value still has to be the default
   * for the search to widen it, so an older hand-picked 25 km stays 25 km.
   */
  radiusChosen?: boolean;
  /** The radius the last search covered, widened or not; see theRadiusSearchedMeter. */
  searchedRadiusMeter?: number;
  /** True when the last search widened the default radius to find enough. */
  widenedRadius?: boolean;
  durations: FilterRangeNumber;
  distances: FilterRangeNumber;
  sportsSelected: string[];
  /** `| null` because the defaults below clear it with null, not undefined. */
  location?: {
    position?: GeoPosition;
    locality?: GeoLocality;
  } | null;
  /**
   * The old « only my level » toggle. Kept optional, and no longer written, so
   * a state persisted before 22/10/2026 still reads: on was the exact level.
   */
  includeMyLevel?: boolean;
  /** Read through theLevelFilterOf, never directly: absent is the default. */
  levelFilter?: LevelFilter;
  /** Keep the « Tous niveaux » activities. Absent means yes. */
  keepAnyLevel?: boolean;
  /** Read through theSearchOrderOf: absent is the recommended order. */
  searchOrder?: SearchOrder;
  handiFriendlyOnly: boolean;
  /**
   * Only brand partner events, from the « Partenaires » link. Optional: a
   * persisted state from before it reads as undefined, which is off.
   */
  partnersOnly?: boolean;
  allDatesPreSelected: { label: string; value: FilterDateTypeExplore }[];
}

const defaults: FilterEventsStateModel = {
  endDate: undefined,
  startDate: getStartEndStartFromFilterDateType('today').startDate,
  distances: {
    min: 0,
    max: 10,
  },
  durations: {
    min: 30 * 60, // 30mn
    max: 60 * 60 * 8, // 8h
  },
  loading: false,
  eventsWrappedBySport: [],
  events: [],
  filtersDateTypeExplore: [],
  radius: radiusKmToMeter(DEFAULT_EXPLORE_RADIUS_KM),
  radiusChosen: false,
  searchedRadiusMeter: undefined,
  widenedRadius: false,
  sportsSelected: [],
  location: null,
  levelFilter: DEFAULT_LEVEL_FILTER,
  keepAnyLevel: true,
  searchOrder: DEFAULT_SEARCH_ORDER,
  handiFriendlyOnly: false,
  partnersOnly: false,
  allDatesPreSelected: [
    {
      label: 'TODAY',
      value: 'today',
    },
    {
      label: 'TOMORROW',
      value: 'tomorrow',
    },
    {
      label: 'WEEK',
      value: 'week',
    },
    {
      label: 'WEEK_END',
      value: 'week-end',
    },
  ],
};

export const filterEventsStateName = 'filterEvents';

@State<FilterEventsStateModel>({
  name: filterEventsStateName,
  defaults,
})
@Injectable()
export class FilterEventsState {
  constructor(
    private readonly _events: EventsService,
    private readonly _store: Store,
    private readonly _analytics: AnalyticsService,
    private readonly _journey: JourneyEventService,
  ) {}

  @Action(SetLoadingEventsAction)
  async setLoadingEventsAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { loading }: SetLoadingEventsAction,
  ) {
    patchState({
      loading,
    });
  }

  @Action(LoadExploreEventsAction)
  async loadExploreEventsActionFromFilter(
    { dispatch, getState }: StateContext<FilterEventsStateModel>,
    _action: LoadExploreEventsAction,
  ) {
    const state = getState();
    const locationFields = buildLocationSearchFields(state);
    return dispatch(
      new FilterExploreEventsAction({
        date: { startDate: startOfToday().toISOString() },
        ...locationFields,
      }),
    );
  }

  @Action(LoadExploreEventsAction)
  async loadExploreEventsAction(
    { dispatch, getState }: StateContext<FilterEventsStateModel>,
    _action: LoadExploreEventsAction,
  ) {
    const location = this._store.selectSnapshot(UserProfileSelector.location);
    return dispatch(
      new FilterExploreEventsAction({
        date: { startDate: startOfToday().toISOString() },
        location: location && {
          lat: location.lat,
          lon: location.lng,
          name: '',
        },
        radiusMeter: location && getState().radius,
      }),
    );
  }

  @Action(LoadExploreEventsActionWithoutMe)
  async loadExploreEventsActionWithoutMe(
    { dispatch, getState }: StateContext<FilterEventsStateModel>,
    _action: LoadExploreEventsAction,
  ) {
    const state = getState();
    const locationFields = buildLocationSearchFields(state);
    return dispatch(
      new FilterExploreEventsAction({
        date: { startDate: startOfToday().toISOString() },
        notFromMe: true,
        notJoined: true,
        ...locationFields,
      }),
    );
  }

  @Action(FilterBySpecificDateExploreEventsAction)
  async filterBySpecificDateExploreEventsAction(
    { patchState, dispatch, getState }: StateContext<FilterEventsStateModel>,
    { date }: FilterBySpecificDateExploreEventsAction,
  ) {
    patchState({
      filtersDateTypeExplore: [],
      startDate: startOfDay(date).toISOString(),
      endDate: endOfDay(startOfDay(date)).toISOString(),
    });
  }

  @Action(FilterByDateExploreEventsAction)
  async filterByDateExploreEventsAction(
    { dispatch, patchState, getState }: StateContext<FilterEventsStateModel>,
    // No `| null`: NGXS hands the action it dispatched, never null, and the
    // union stopped the destructuring from compiling at all. `dateType` itself
    // can be absent, which the branch below already tests.
    { dateType }: FilterByDateExploreEventsAction,
  ) {
    if (dateType) {
      patchState({
        filtersDateTypeExplore: [dateType],
        startDate: null,
        endDate: null,
      });
    } else {
      patchState({
        filtersDateTypeExplore: ['all'],
        startDate: null,
        endDate: null,
      });
    }
  }

  /**
   * A stretch of days, from the calendar.
   *
   * `filtersDateTypeExplore` is emptied with it: the presets are gone from the
   * screen since SMA-93, but a member who used one before this shipped still
   * carries it in the persisted state, and leaving it beside a range would
   * send two date filters at once.
   */
  @Action(FilterByDateRangeExploreEventsAction)
  async filterByDateRangeExploreEventsAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { start, end }: FilterByDateRangeExploreEventsAction,
  ) {
    patchState({
      filtersDateTypeExplore: [],
      startDate: startOfDay(start).toISOString(),
      endDate: endOfDay(end).toISOString(),
    });
  }

  @Action(ResetByDateExploreEventsAction)
  async resetByDateExploreEventsAction({ patchState }: StateContext<FilterEventsStateModel>) {
    patchState({
      filtersDateTypeExplore: [],
      startDate: null,
      endDate: null,
    });
  }

  @Action(SendFilterEventsAction)
  async sendFilterEventsAction(
    { dispatch, getState }: StateContext<FilterEventsStateModel>,
    _action: SendFilterEventsAction,
  ) {
    const state = getState();

    // `FilterDate[]`, which is what the request carries: its `endDate` is
    // optional there, and the branch below pushes a range whose end may be
    // absent. The helper's own answer always has one, so nothing is lost.
    const dates: FilterDate[] = state.filtersDateTypeExplore.map(getStartEndStartFromFilterDateType) || [];

    if (state.startDate && dates.length === 0) {
      dates.push({
        startDate: state.startDate,
        // The state clears a date with null and the request carries an absent
        // one as undefined; this is the one place the two meet.
        endDate: state.endDate ?? undefined,
      });
    }

    const locationFields = buildLocationSearchFields(state);
    const dto: FilterEventDto = {
      dates,
      ...theLevelAndOrderToSend(state),
      ...(state.handiFriendlyOnly ? { handiFriendlyOnly: true } : {}),
      ...(state.partnersOnly ? { partnersOnly: true } : {}),
      sportIds: state.sportsSelected,
      minDuration: state.durations.min,
      maxDuration: state.durations.max,
      userLocation: this.buildUserLocationForKm(),
      ...locationFields,
    };
    if (!dto.location && dto.searchMode === 'radius') {
      const location = this._store.selectSnapshot(UserProfileSelector.location);
      const geoLocality = this._store.selectSnapshot(UserProfileSelector.locality);
      if (location?.lat && location?.lng && geoLocality?.formattedAddress) {
        dto.location = {
          lat: location.lat,
          lon: location.lng,
          name: geoLocality.formattedAddress,
        };
      } else {
        delete dto.location;
      }
    }
    // `?.length` because the field is optional on the DTO: absent and empty
    // both mean "no sport asked for", and both are removed rather than sent.
    if (!dto.sportIds?.length) {
      delete dto.sportIds;
    }
    return dispatch(new FilterExploreEventsAction(dto));
  }

  @Action(FilterExploreEventsAction)
  async filterExploreEventsAction(
    { patchState, getState }: StateContext<FilterEventsStateModel>,
    { filter }: FilterExploreEventsAction,
  ) {
    patchState({
      loading: true,
    });
    const enrichedFilter: FilterEventDto = {
      ...filter,
      userLocation: filter.userLocation ?? this.buildUserLocationForKm(),
    };
    if (enrichedFilter.radiusMeter != null && enrichedFilter.searchMode !== 'hierarchy') {
      enrichedFilter.radiusMeter = getState().radius;
      // The API widens only when asked, because nothing in a radius in metres
      // tells a default from a choice. An API released before the field drops
      // it (whitelist) and searches the radius as sent.
      if (theSearchMayWiden(enrichedFilter.radiusMeter, getState().radiusChosen === true)) {
        enrichedFilter.widenRadius = true;
      }
    }

    if (enrichedFilter.dates?.length === 0) {
      enrichedFilter.dates = [getStartEndStartFromFilterDateType(undefined)];
    }

    return this._events
      .searchEvents({
        filterEventDto: enrichedFilter,
      })
      .pipe(
        /*
         * THE SCREEN STOPS LOADING WHATEVER HAPPENS, and it did not.
         *
         * `loading: false` was written on the happy path alone: an answer
         * carrying an error, or no value, returned early and left the flag on
         * for good. The explore page reads it to choose between its skeleton
         * and everything else, so it sat on the skeleton for ever -- no
         * activities, and not even the "there is nothing near you" it is
         * supposed to fall back to. Found on a device on 30 September 2026:
         * the API answered the search in 78 ms and the screen stayed blank.
         *
         * `finalize` covers the three ways out at once: the answer, the early
         * return, and an HTTP failure, which had no branch at all.
         */
        finalize(() => patchState({ loading: false })),
        catchError((failure) => {
          // Swallowed rather than rethrown, as the rest of this state does: a
          // search that fails leaves the page on its empty state, and the
          // member can search again. `finalize` above has already cleared the
          // flag, so nothing stays stuck.
          console.error('The activity search failed', failure);

          return EMPTY;
        }),
        tap((value) => {
          // `!value.value` as well: testing the error alone narrows nothing,
          // because both fields are optional on the wrapper, so the reads below
          // were unchecked. A result carrying neither is not expected and now
          // leaves rather than throwing inside the stream.
          if (value.error || !value.value) {
            console.error('The activity search answered without a result', value.error);
            return;
          }

          // Read once into a local: the guard above narrows `value.value` here
          // but not inside the closure below, which reads it again.
          const found = value.value.events;
          const uniqueSportKeyIds: string[] = [...new Set(found.map((d) => d.sportKeyId))];
          const wrappedBySportIds: { sportKeyId: string; events: ActiveEventDetailOutputDto[] }[] =
            uniqueSportKeyIds.reduce((acc, sportId) => {
              acc.push({
                sportKeyId: sportId,
                events: found.filter((d) => d.sportKeyId === sportId),
              });
              return acc;
            }, [] as { sportKeyId: string; events: ActiveEventDetailOutputDto[] }[]);
          // A search returning zero results is demand with no supply behind it,
          // which is exactly what turns the city ranking from descriptive into
          // predictive. Hence logging the count, including when it is zero.
          this._analytics.logSearchPerformed({
            querySport: filter.sportIds?.length === 1 ? filter.sportIds[0] : undefined,
            queryCity: filter.location?.name,
            queryDate: filter.dates?.[0]?.startDate,
            resultsCount: value.value.events.length,
          });
          // Same moment, our own funnel (taux 4): a search ran. Session-deduped
          // in the service, so repeated filtering records the step once.
          this._journey.emit('search_performed');
          patchState({
            loading: false,
            eventsWrappedBySport: wrappedBySportIds,
            events: value.value.events,
            searchedRadiusMeter:
              enrichedFilter.searchMode === 'hierarchy'
                ? undefined
                : theRadiusSearchedMeter(value.value.radiusMeter, enrichedFilter.radiusMeter),
            // `=== true`: absent from an API that predates it, and such an API
            // never widens.
            widenedRadius: value.value.widenedRadius === true,
          });
        }),
      );
  }

  @Action(SetDurationFiltersEventAction)
  async setDurationFilterAction(
    { patchState, dispatch }: StateContext<FilterEventsStateModel>,
    { payload }: SetDurationFiltersEventAction,
  ) {
    patchState({
      durations: payload,
    });
  }

  @Action(SetDistanceFiltersEventAction)
  async setDistanceFilterAction(
    { patchState, dispatch }: StateContext<FilterEventsStateModel>,
    { payload }: SetDistanceFiltersEventAction,
  ) {
    patchState({
      distances: payload,
      radius: (payload.max - payload.min) * 1000,
      // The distance slider of the filters: a radius the person set.
      radiusChosen: true,
    });
  }

  @Action(UpdateRadiusExploreAction)
  async updateRadiusExploreAction(
    { patchState, dispatch }: StateContext<FilterEventsStateModel>,
    { payload, skipSearch, chosenByPerson }: UpdateRadiusExploreAction,
  ) {
    patchState({
      radius: radiusKmToMeter(payload),
      radiusChosen: chosenByPerson,
    });

    if (!skipSearch) {
      dispatch(new SendFilterEventsAction());
    }
  }

  @Action(AddSportSelectedToFilterEventsAction)
  async addSportSelectedToFilterAction(
    { patchState, getState, dispatch }: StateContext<FilterEventsStateModel>,
    { sportId, resetOther }: AddSportSelectedToFilterEventsAction,
  ) {
    if (resetOther) {
      patchState({
        sportsSelected: [sportId],
      });
    } else {
      patchState({
        sportsSelected: [...new Set([...getState().sportsSelected, sportId])],
      });
    }
  }

  @Action(RemoveSportSelectedToFilterEventsAction)
  async removeSportSelectedToFilterAction(
    { patchState, getState, dispatch }: StateContext<FilterEventsStateModel>,
    { sportId }: RemoveSportSelectedToFilterEventsAction,
  ) {
    patchState({
      sportsSelected: [...getState().sportsSelected.filter((s) => s !== sportId)],
    });
  }

  @Action(SetLevelFilterEventsAction)
  async setLevelFilterEventsAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { payload }: SetLevelFilterEventsAction,
  ) {
    patchState({
      ...payload,
      // Written false once the new choice exists, so the old toggle never
      // speaks again for this state.
      includeMyLevel: false,
    });
  }

  @Action(SetHandiFriendlyOnlyFilterEventsAction)
  async setHandiFriendlyOnlyFilterEventsAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { payload }: SetHandiFriendlyOnlyFilterEventsAction,
  ) {
    patchState({
      handiFriendlyOnly: payload === true,
    });
  }

  @Action(SetPartnersOnlyFilterEventsAction)
  setPartnersOnlyFilterEventsAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { payload }: SetPartnersOnlyFilterEventsAction,
  ) {
    patchState({ partnersOnly: payload === true });
  }

  @Action(SetLocationFilterEventsAction)
  async setLocationFilterEventsAction(
    { patchState, getState, dispatch }: StateContext<FilterEventsStateModel>,
    { payload }: SetLocationFilterEventsAction,
  ) {
    const position = resolveFilterPosition({
      locality: payload.locality,
      position: payload.position,
    });

    patchState({
      location: position
        ? {
            locality: payload.locality,
            position,
          }
        : payload,
    });
  }

  @Action(SetFilterByDateExploreEventsAction)
  setFilterByDateExploreEventsAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { dateType }: SetFilterByDateExploreEventsAction,
  ) {
    patchState({
      filtersDateTypeExplore: [dateType],
    });
  }

  @Action(FilterByDateExploreAction)
  async setFilterByDateExploreAction(
    { patchState }: StateContext<FilterEventsStateModel>,
    { dateType }: SetFilterByDateExploreEventsAction,
  ) {
    const filterLocation = this._store.selectSnapshot(FilterEventsSelector.location);

    patchState({
      distances: defaults.distances,
      durations: defaults.durations,
      sportsSelected: [],
      partnersOnly: false,
      endDate: undefined,
      filtersDateTypeExplore: [dateType],
      location: filterLocation,
    });
  }

  @Action(ResetFilterEventsAction)
  async resetFilterAction({ patchState }: StateContext<FilterEventsStateModel>) {
    patchState({
      handiFriendlyOnly: false,
      partnersOnly: false,
      includeMyLevel: false,
      levelFilter: DEFAULT_LEVEL_FILTER,
      keepAnyLevel: true,
      searchOrder: DEFAULT_SEARCH_ORDER,
      distances: defaults.distances,
      durations: defaults.durations,
      startDate: undefined,
      endDate: undefined,
      filtersDateTypeExplore: ['all'],
      sportsSelected: [],
    });
  }

  private buildUserLocationForKm(): FilterEventDto['userLocation'] {
    const userLocation = this._store.selectSnapshot(UserProfileSelector.location);
    if (userLocation?.lat == null || userLocation?.lng == null) {
      return undefined;
    }
    return { lat: userLocation.lat, lon: userLocation.lng };
  }
}
